const FULLSCREEN_CHANGE_EVENTS = [
    'fullscreenchange',
    'webkitfullscreenchange',
];
const FULLSCREEN_ERROR_EVENTS = [
    'fullscreenerror',
    'webkitfullscreenerror',
];

export class FullscreenController {
    constructor({
        element,
        documentLike = globalThis.document,
    }) {
        if (!element) {
            throw new TypeError('O elemento do modo tela cheia é obrigatório.');
        }

        this.element = element;
        this.document = documentLike;
        this.listeners = new Set();
        this.pendingEnter = null;
        this.pendingExit = null;
        this.lastError = null;

        this.handleChange = this.handleChange.bind(this);
        this.handleError = this.handleError.bind(this);

        for (const eventName of FULLSCREEN_CHANGE_EVENTS) {
            this.document?.addEventListener?.(
                eventName,
                this.handleChange
            );
        }

        for (const eventName of FULLSCREEN_ERROR_EVENTS) {
            this.document?.addEventListener?.(
                eventName,
                this.handleError
            );
        }
    }

    get active() {
        const fullscreenElement = (
            this.document?.fullscreenElement
            || this.document?.webkitFullscreenElement
        );

        return fullscreenElement === this.element;
    }

    get supported() {
        return Boolean(this.getRequestMethod());
    }

    getRequestMethod() {
        if (
            this.document?.fullscreenEnabled !== false
            && typeof this.element.requestFullscreen === 'function'
        ) {
            return {
                method: this.element.requestFullscreen,
                options: { navigationUI: 'hide' },
            };
        }

        const webkitRequest = (
            this.element.webkitRequestFullscreen
            || this.element.webkitRequestFullScreen
        );

        if (typeof webkitRequest === 'function') {
            return {
                method: webkitRequest,
                options: undefined,
            };
        }

        return null;
    }

    getExitMethod() {
        const exit = (
            this.document?.exitFullscreen
            || this.document?.webkitExitFullscreen
            || this.document?.webkitCancelFullScreen
        );

        return typeof exit === 'function' ? exit : null;
    }

    snapshot() {
        return {
            active: this.active,
            supported: this.supported,
            error: this.lastError,
        };
    }

    notify() {
        const state = this.snapshot();

        for (const listener of this.listeners) {
            listener(state);
        }
    }

    handleChange() {
        this.lastError = null;
        this.notify();
    }

    handleError(event) {
        this.lastError = (
            event?.error
            || new Error('O navegador recusou o modo tela cheia.')
        );
        this.notify();
    }

    onChange(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    enter() {
        if (this.active || !this.supported) {
            return Promise.resolve(this.snapshot());
        }

        if (this.pendingEnter) {
            return this.pendingEnter;
        }

        const request = this.getRequestMethod();
        this.lastError = null;

        try {
            const requestResult = request.options
                ? request.method.call(this.element, request.options)
                : request.method.call(this.element);

            this.pendingEnter = Promise.resolve(requestResult)
                .then(() => {
                    this.notify();
                    return this.snapshot();
                })
                .catch((error) => {
                    this.lastError = error;
                    this.notify();
                    return this.snapshot();
                })
                .finally(() => {
                    this.pendingEnter = null;
                });
        } catch (error) {
            this.lastError = error;
            this.notify();
            return Promise.resolve(this.snapshot());
        }

        return this.pendingEnter;
    }

    exit() {
        const exitMethod = this.getExitMethod();

        if (!this.active || !exitMethod) {
            return Promise.resolve(this.snapshot());
        }

        if (this.pendingExit) {
            return this.pendingExit;
        }

        this.lastError = null;

        try {
            const exitResult = exitMethod.call(this.document);

            this.pendingExit = Promise.resolve(exitResult)
                .then(() => {
                    this.notify();
                    return this.snapshot();
                })
                .catch((error) => {
                    this.lastError = error;
                    this.notify();
                    return this.snapshot();
                })
                .finally(() => {
                    this.pendingExit = null;
                });
        } catch (error) {
            this.lastError = error;
            this.notify();
            return Promise.resolve(this.snapshot());
        }

        return this.pendingExit;
    }

    destroy() {
        for (const eventName of FULLSCREEN_CHANGE_EVENTS) {
            this.document?.removeEventListener?.(
                eventName,
                this.handleChange
            );
        }

        for (const eventName of FULLSCREEN_ERROR_EVENTS) {
            this.document?.removeEventListener?.(
                eventName,
                this.handleError
            );
        }

        this.listeners.clear();
    }
}
